<template>
    <div
        id="form-page"
        v-bind:class="{
            'already-answered': is('alreadyAnswered') || is('hasUnusedCoupon'),
            'embed': embed
        }"
    >
        <header v-if="!is('loading') && !embed">
            <img :src="place.logo" alt=""/>
        </header>
        <div class="container-fluid">
            <div class="row justify-content-center">
                <div class="col-12 form-column">
                    <form action="" @submit.prevent novalidate>
                        <template v-if="is('loading')">
                            <div class="intro">
                                <span class="fa fa-spin fa-spinner"></span>
                            </div>
                        </template>
                        <template v-if="is('answering')">
                            <div class="form-intro" v-if="title || description">
                                <h3 v-if="title">{{ title }}</h3>
                                <p v-if="description">{{ description }}</p>
                            </div>
                            <div
                                class="steps"
                                ref="stepsHolder"
                                :class="{ 'client-fields-above': clientFieldsAbove }"
                            >
                                <form-step
                                    ref="steps"
                                    v-for="(step, key) in steps"
                                    :key="step.id"
                                    :questions="step.questions"
                                    :form-id="id"
                                    :class="{ active: isStepActive(key) }"
                                    @scroll="scrollStep"
                                />
                                <div
                                    class="form-step active form-step-client"
                                    v-if="showClientForm"
                                >
                                    <user-form
                                        :client="client"
                                        :place="place"
                                        :fields-above="clientFieldsAbove"
                                        @saved="clientUpdated"
                                        ref="formUser"
                                    ></user-form>
                                </div>
                            </div>
                            <div class="save-error" v-if="saveError" role="alert">
                                {{ saveError }}
                            </div>
                            <button
                                type="button"
                                class="btn btn-lg btn-success pull-right btn-continue"
                                @click.prevent="next"
                                :disabled="saving"
                            >
                                <span
                                    class="fa fa-spinner fa-spin"
                                    v-if="saving"
                                ></span>
                                {{ t('form-submit') }}
                            </button>
                        </template>
                        <template v-if="is('saved')">
                            <div
                                class="thanks"
                                v-bind:style="{ background: place.color }"
                            >
                                <template v-if="coupon">
                                    <div class="coupon alert alert-info">
                                        <div
                                            class="prize"
                                            v-html="couponPrizeHtml"
                                        ></div>
                                        <div class="coupon-code">
                                            {{ coupon.code }}
                                        </div>
                                        <div class="note">
                                            {{ t('form-coupon_valid_until', { date: coupon.expiration_date }) }} <br/>
                                        </div>
                                    </div>
                                    <div class="how-to-receive">
                                        {{ coupon.how_to_use }}
                                        <div class="kevi-credits">
                                            {{ t('form-developed_with') }}
                                            <a
                                                href="https://kevi.io"
                                                target="_blank"
                                            >Kevi.io</a
                                            >
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="coupon alert alert-info">
                                        <div class="coupon-code">{{ successTitle }}</div>
                                        <div class="note">
                                            {{ successMessage }}
                                        </div>
                                    </div>
                                    <div class="kevi-credits">
                                        {{ t('form-developed_with') }}
                                        <a
                                            href="https://kevi.io"
                                            target="_blank"
                                        >Kevi.io</a
                                        >
                                    </div>
                                </template>
                            </div>
                        </template>
                        <template v-if="is('notFound')">
                            <div class="intro">
                                <span>
                                    <span class="fa fa-close"></span> {{ t('form-not_found') }}</span
                                >
                            </div>
                        </template>
                        <template v-if="is('alreadyAnswered')">
                            <div class="intro already-answered">
                                <div class="block">
                                    <span class="icon icon-sad"></span> <br/>
                                    <span
                                        class="error-message"
                                        v-html="alreadyAnsweredHtml"
                                    ></span>
                                </div>
                            </div>
                        </template>
                        <template v-if="is('hasUnusedCoupon')">
                            <div class="intro already-answered">
                                <div class="block">
                                    <span class="icon icon-sad"></span> <br/>
                                    <span class="error-message">
                                        {{ t('form-unused_coupon') }}
                                    </span>
                                </div>
                            </div>
                        </template>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.embed#form-page {
    background: transparent;
}
.form-column {
    max-width: 1280px;
}
.save-error {
    margin: 16px 15px 0;
    padding: 10px 14px;
    border-radius: 8px;
    background: #fff1f1;
    color: #c4363b;
    font-size: 14px;
}
.steps.client-fields-above {
    display: flex;
    flex-direction: column;
}
.steps.client-fields-above .form-step:not(.active) {
    display: none;
}
.steps.client-fields-above .form-step-client {
    order: -1;
    margin-bottom: 0;
}
</style>
<script>
import keviApi from "./../keviApi";
import FormStep from "./step";
import FormUser from "./form-user";
import { applyPlaceLanguage } from "./../i18n";

function escapeHtml(value) {
    return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

export default {
    name: "Form",
    props: {
        client: Object,
    },
    data: function () {
        return {
            title: "",
            description: "",
            steps: [],
            stepActive: 0,
            state: "loading",
            id: null,
            saving: false,
            saveError: null,
            lastAnswer: null,
            nextAnswer: null,
            embed: false,
            custom_css: null,
            clientFieldsPosition: "below",
            customSuccessTitle: "",
            customSuccessMessage: "",
            coupon: {
                code: null,
                expiration_date: null,
                text: null,
                how_to_use: null,
            },
        };
    },
    components: {
        "form-step": FormStep,
        "user-form": FormUser,
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
        form() {
            return this.$store.state.form;
        },
        branchId() {
            return this.$route.query.branch_id || null;
        },
        clientFieldsAbove() {
            return this.clientFieldsPosition === "above";
        },
        successTitle() {
            return this.customSuccessTitle || this.t("form-thanks_title");
        },
        successMessage() {
            return this.customSuccessMessage || this.t("form-thanks_message");
        },
        couponPrizeHtml() {
            return this.t("form-coupon_prize", {
                name: escapeHtml(this.client.first_name),
                prize: escapeHtml(this.coupon && this.coupon.text),
            });
        },
        alreadyAnsweredHtml() {
            return this.t("form-already_answered", {
                date: this.formatDate(this.lastAnswer),
                next: this.formatDateTime(this.nextAnswer),
            });
        },
        showClientForm: function () {
            const form = this.form || {};
            const client = this.client || {};
            if (form.askName !== "no" && !client.first_name) {
                return true;
            }
            if (form.askLastName !== "no" && !client.last_name) {
                return true;
            }
            if (form.askEmail !== "no" && !client.email) {
                return true;
            }
            if (form.askPhone !== "no" && !client.whatsapp) {
                return true;
            }
            if (form.askBirthdate !== "no" && !client.birthdate) {
                return true;
            }
            return false;
        },
    },
    created: function () {
        if (this.place && this.place.locale) {
            applyPlaceLanguage(this.place, this.$root);
        }
        document.getElementsByTagName("title")[0].innerText =
            this.t("form-page_title");
        this.updateForm();
        this.embed = parseInt(this.$route.query.embed) === 1;
        if (this.$route.params.place) {
            var options = keviApi.getOptions(
                "place/" + this.$route.params.place + "/public-info",
            );
            this.$http(options).then(
                function (response) {
                    if (response.status === 200) {
                        this.loading = false;
                        if (!response.body.logo) {
                            response.body.logo = "images/kevi.png";
                        }
                        if (!response.body.color) {
                            response.body.color = "#0f2f67";
                        } else {
                            response.body.color = "#" + response.body.color;
                        }
                        this.image = response.body.logo;
                        this.color = response.body.color;
                        applyPlaceLanguage(response.body, this.$root);
                        document.getElementsByTagName("title")[0].innerText =
                            this.t("form-page_title");
                        this.$store.commit("setPlace", response.body);
                        this.$emit("found-place", response.body);
                    }
                },
                function () {
                    this.loading = false;
                },
            );
        }
    },
    methods: {
        isStepActive: function (step) {
            return this.stepActive === step ? true : false;
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        begin: function () {
            this.setState("answering");
        },
        next: function () {
            if (this.$refs.steps[this.stepActive].isValid()) {
                if (this.showClientForm) {
                    if (this.$refs.formUser.isValid()) {
                        this.save();
                    }
                } else {
                    this.save();
                }
            } else {
                this.$nextTick(() => this.showFirstError());
            }
        },
        save: function () {
            window.mixpanel?.track("Form save begin");
            this.saving = true;
            this.saveError = null;
            var data = this.getData();
            var options = keviApi.getOptions("answers", "PUT", data);
            // In the body, not the query string: a table answer easily passes
            // the server's 8 KB request-line limit (HTTP 414).
            options.body = options.params;
            options.params = {};
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.saving = false;
                    this.coupon = response.body?.coupon;
                    if (response.body?.client) {
                        this.foundClient(response.body.client);
                    }
                  window.parent.postMessage({ type: "kevi-form-submitted", formId: this.id, result: response.body  }, "*");

                  this.setState("saved");
                    window.mixpanel?.track("Form saved", {
                        coupon: this.coupon,
                    });
                }
            }, (response) => {
                // Without this the button kept spinning forever on any error.
                this.saving = false;
                const body = (response && response.body) || {};
                this.saveError = body.message === "invalid-table-answer"
                    ? this.t("form-save_error_table")
                    : this.t("form-save_error");
                window.mixpanel?.track("Form save failed", {
                    status: response && response.status,
                    message: body.message,
                });
            });
        },
        foundClient: function (data) {
            this.$emit("found-client", data);
        },
        getData: function () {
            let data = {
                forms_id: this.id,
                clients_id: this.client.id,
                steps_questions: {},
            };
            if (this.$store.state.linkCode) {
                data.link_code = this.$store.state.linkCode.link_code;
            }
            if (this.branchId) {
                data.branch_id = this.branchId;
            }
            for (let s in this.$refs.steps) {
                let step = this.$refs.steps[s];
                for (let q in step.$refs.questions) {
                    let question = step.$refs.questions[q];
                    if (!question.isNull()) {
                        data["steps_questions"][question.name] =
                            question.getValue();
                    }
                }
            }
            if (this.showClientForm) {
                data.user = this.$refs.formUser.getData();
            }
            return data;
        },
        updateForm: function () {
            window.mixpanel?.track("Form loading");
            let data = {
                clients_id: this.client.clients_id,
            };
            let place =
                this.$route.params.place || this.$store.state.place.slug;
            if (this.$store.state.linkCode) {
                data.link_code = this.$store.state.linkCode.link_code;
            }
            if (this.$route.params.formId) {
                data.formId = this.$route.params.formId;
            }

            var options = keviApi.getOptions(
                "place/" + place + "/form",
                "GET",
                data,
            );
            this.$http(options).then(
                function (response) {
                    if (response.status === 200) {
                        window.mixpanel?.track("Form loaded");
                        if (response.body?.status === "success") {
                            this.$store.commit("clearAnswers");
                            this.$store.commit("setForm", response.body.form);
                            this.id = response.body.form.id;
                            this.steps = response.body.form.steps;
                            this.title = response.body.form.title;
                            this.description = response.body.form.description;
                            this.custom_css = response.body.form.custom_css;
                            this.clientFieldsPosition =
                                response.body.form.client_fields_position ||
                                "below";
                            this.customSuccessTitle =
                                response.body.form.success_title || "";
                            this.customSuccessMessage =
                                response.body.form.success_message || "";
                            window.mixpanel?.track("Form answer begin");
                            this.setState("answering");
                        } else if (
                            response.body?.status === "already-answered"
                        ) {
                            this.lastAnswer = new Date(
                                response.body.last_answer,
                            );
                            this.nextAnswer = new Date(
                                response.body.next_answer,
                            );
                            window.mixpanel?.track("Form already answered");
                            this.setState("alreadyAnswered");
                        } else if (response.body?.status === "unused-coupon") {
                            window.mixpanel?.track("Has unused coupon");
                            this.setState("hasUnusedCoupon");
                        } else {
                            this.setState("notFound");
                        }
                    }
                },
                function () {
                    this.setState("notFound");
                },
            );
        },

        scrollStep: function (scroll) {
            window.scrollTo({ top: Math.max(scroll - 16, 0), behavior: "smooth" });
        },

        // Brings the first invalid question (or table cell) into view instead
        // of jumping back to the top of the form.
        showFirstError: function () {
            const step = this.$refs.steps[this.stepActive];
            const el = step && step.firstInvalidElement();
            if (!el) {
                this.scrollStep(0);
                return;
            }
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            if (typeof el.focus === "function" && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) {
                el.focus({ preventScroll: true });
            }
        },

        postHeight: function () {
            const form = document.querySelector("#form-page");
            if (!form) return;
            window.parent.postMessage({ type: "kevi-form-resize", formId: this.id, height: window.getComputedStyle(form).height }, "*");
        },
        clientUpdated: function (client) {
            this.$emit("found-client", client);
            this.setState("saved");
        },
        formatDate: function (date) {
            if (!date) {
                return "";
            }
            return new Intl.DateTimeFormat(this.$root.locale || "pt-BR", {
                day: "numeric",
                month: "numeric",
            }).format(date);
        },
        formatDateTime: function (date) {
            if (!date) {
                return "";
            }
            return new Intl.DateTimeFormat(this.$root.locale || "pt-BR", {
                day: "numeric",
                month: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }).format(date);
        },
    },
    mounted: function () {
        // Embedded forms: tell the parent page every time the height changes
        // (rows added to a table, conditional questions appearing…), not only
        // when the steps load.
        if (window.parent !== window && typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(() => this.postHeight());
            this.resizeObserver.observe(this.$el);
        }
    },
    beforeDestroy: function () {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    watch: {
        steps: {
          handler() {
            //emit event to parent frame
            //if there is an iframe parent
            if (window.parent !== window) {
              this.$nextTick(() => {
                const form = document.querySelector("#form-page");
                //get height + scroll height
                const totalHeight = window.getComputedStyle(form).height;

                window.parent.postMessage({ type: "kevi-form-resize", formId: this.id, height: totalHeight }, "*");
              });
            }
          },
        },
        custom_css: {
            handler(newCss) {
                if (this.embed) {
                    let styleElement = document.getElementById("dynamic-custom-css");
                    if (!styleElement) {
                        styleElement = document.createElement("style");
                        styleElement.id = "dynamic-custom-css";
                        document.head.appendChild(styleElement);
                    }
                    // Set the CSS content
                    styleElement.innerHTML = newCss;
                }
            }
        }
    }
};
</script>
